import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { Check } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { ModalShell } from '../ui';


export default function ShareModal({ show, onClose, currentPost }) {
  const { t, isDark } = useTheme();
  const [copied, setCopied] = useState(false);
  const [instaCopied, setInstaCopied] = useState(false);

  // Track share count in database
  const trackShare = async () => {
    if (!currentPost?.id) return;

    try {
      await supabase
        .from('posts')
        .update({ share_count: (currentPost.share_count || 0) + 1 })
        .eq('id', currentPost.id);
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  };

  // Use /share/ URL for social media (serves OG meta tags to crawlers)
  const getShareUrl = () => `${window.location.origin}/share/${currentPost.id}`;
  
  // Use ?post= URL for direct linking (no OG tags needed)
  const getDirectUrl = () => `${window.location.origin}?post=${currentPost.id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      trackShare();
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleShareX = () => {
    const url = getShareUrl();
    const text = `Check out "${currentPost.title}" on Limestone`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    trackShare();
  };

  const handleShareLinkedIn = () => {
    const url = getShareUrl();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    trackShare();
  };

  const handleShareInstagram = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setInstaCopied(true);
      trackShare();
      setTimeout(() => setInstaCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  if (!show) return null;

  return (
    <ModalShell onClose={onClose} panelStyle={{ padding: '40px', borderRadius: '20px' }}>
        <h2 style={{ 
          fontFamily: 'Crimson Pro, serif', 
          fontSize: '32px', 
          marginBottom: '16px',
          color: t.text,
          textAlign: 'center'
        }}>
          Share Post
        </h2>
        <p style={{ 
          color: t.textSecondary, 
          marginBottom: '32px',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          Share "{currentPost?.title}" on social media
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          {/* X (Twitter) Button */}
          <button
            onClick={handleShareX}
            style={{
              backgroundColor: '#000000',
              color: t.surface,
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Poppins, sans-serif',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            𝕏 X
          </button>

          {/* LinkedIn Button */}
          <button
            onClick={handleShareLinkedIn}
            style={{
              backgroundColor: '#0A66C2',
              color: t.surface,
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Poppins, sans-serif',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            LinkedIn
          </button>

          {/* Instagram Button */}
          <button
            onClick={handleShareInstagram}
            style={{
              background: instaCopied
                ? t.success
                : 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
              color: t.surface,
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {instaCopied ? 'Copied! Paste in bio or story' : 'Instagram'}
          </button>

          {/* Copy Link Button */}
          <button
            onClick={handleCopyLink}
            style={{
              backgroundColor: copied ? t.success : t.buttonBg,
              color: t.buttonText,
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {copied ? (
              <>
                <Check size={18} />
                Copied!
              </>
            ) : (
              'Copy Link'
            )}
          </button>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            backgroundColor: 'transparent',
            color: t.text,
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${t.borderStrong}`,
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: 'Poppins, sans-serif',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.bgSubtle}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          Close
        </button>
    </ModalShell>
  );
}
